import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CheckCircle2, Info, TriangleAlert, X } from 'lucide-react';

const ToastContext = createContext({ showToast: () => {} });

const toneConfig = {
  success: {
    icon: CheckCircle2,
    classes: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  },
  error: {
    icon: TriangleAlert,
    classes: 'border-rose-200 bg-rose-50 text-rose-900',
  },
  info: {
    icon: Info,
    classes: 'border-blue-200 bg-blue-50 text-blue-900',
  },
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const dismissToast = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback(({ title, message, tone = 'info', duration = 3500 }) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setToasts((current) => [...current, { id, title, message, tone }]);
    window.setTimeout(() => dismissToast(id), duration);
  }, [dismissToast]);

  const value = useMemo(() => ({ showToast, dismissToast }), [showToast, dismissToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed right-4 top-4 z-[100] flex w-full max-w-sm flex-col gap-3">
        {toasts.map((toast) => {
          const config = toneConfig[toast.tone] || toneConfig.info;
          const Icon = config.icon;
          return (
            <div key={toast.id} className={`pointer-events-auto rounded-2xl border p-4 shadow-lg ${config.classes}`}>
              <div className="flex items-start gap-3">
                <Icon size={18} className="mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  {toast.title ? <p className="font-semibold">{toast.title}</p> : null}
                  {toast.message ? <p className="mt-1 text-sm opacity-90">{toast.message}</p> : null}
                </div>
                <button type="button" onClick={() => dismissToast(toast.id)} className="rounded-md p-1 opacity-70 hover:opacity-100">
                  <X size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
